import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { createContext, useContext } from 'react';

import { toggleVariants } from '@/components/ui/toggle';

const toggleGroupVariants = cva('group/toggle-group flex w-fit items-center', {
  variants: {
    groupVariant: {
      default: 'rounded-md data-[variant=outline]:shadow-xs',
      separate: 'gap-2',
    },
  },
  defaultVariants: {
    groupVariant: 'default',
  },
});

type ToggleGroupType = VariantProps<typeof toggleVariants> &
  VariantProps<typeof toggleGroupVariants> & {
    alwaysOneSelected?: boolean;
  };

const ToggleGroupContext = createContext<ToggleGroupType>({
  size: 'md',
  variant: 'default',
  groupVariant: 'default',
  alwaysOneSelected: false,
});

export function ToggleGroup(toggleGroupProps: ComponentProps<typeof ToggleGroupPrimitive.Root> & ToggleGroupType) {
  const { className, variant, size, children, groupVariant = 'default', alwaysOneSelected = false, onValueChange, ...props } = toggleGroupProps;
  const onValueChangeCallback = (value: string & string[]) => {
    const isArray: boolean = Array.isArray(value);
    const hasValue: boolean = isArray ? value.length > 0 : !!value;

    if (alwaysOneSelected && !hasValue) {
      return;
    }
    if (onValueChange) {
      onValueChange(value);
    }
  };

  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-group-variant={groupVariant}
      onValueChange={onValueChangeCallback}
      className={cn(toggleGroupVariants({ groupVariant: groupVariant }), className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, groupVariant }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

export function ToggleGroupItem(toggleGroupItemProps: ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  const { className, children, variant, size, ...props } = toggleGroupItemProps;
  const context = useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-group-variant={context.groupVariant}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        context.groupVariant === 'default' &&
          'min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l',
        context.groupVariant === 'separate' && 'rounded-md',
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}
